import { useEffect, useState } from 'react';
import { Modal } from 'react-bootstrap';
import { useAlert } from 'react-alert';
import { personnelApi, errMsg } from './personnelApi';

const EMPTY = {
  firstName: '',
  middleName: '',
  lastName: '',
  email: '',
  phone: '',
  role: '',
  organizationIds: [],
  credential: '',
  passwordMode: 'invite',
  password: '',
  confirmPassword: '',
};
const STRONG = /^(?=.*[A-Za-z])(?=.*\d).{8,128}$/;

// Add Personnel: "invite" (default) emails a set-password link; "temporary" (Super Admin only) saves the typed password.
const AddPersonnelModal = ({ show, onHide, options, onCreated }) => {
  const alert = useAlert();
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [showPw, setShowPw] = useState(false);

  useEffect(() => {
    if (show) {
      setForm(EMPTY);
      setError(null);
      setShowPw(false);
    }
  }, [show]);

  const roles = options?.roles || [];
  const orgs = options?.organizations || [];
  const role = roles.find((r) => r.value === form.role);
  const canSetPassword = !!options?.canSetPassword;
  const temporary = canSetPassword && form.passwordMode === 'temporary';
  const weak = temporary && form.password.length > 0 && !STRONG.test(form.password);
  const mismatch = temporary && form.confirmPassword.length > 0 && form.password !== form.confirmPassword;
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const toggleOrg = (id) =>
    setForm((f) => ({
      ...f,
      organizationIds: f.organizationIds.includes(id) ? f.organizationIds.filter((o) => o !== id) : [...f.organizationIds, id],
    }));

  const submit = async (e) => {
    e.preventDefault();
    if (role?.requiresOrganization && form.organizationIds.length === 0) {
      setError('Select at least one organization');
      return;
    }
    if (temporary && (!form.password || weak || form.password !== form.confirmPassword)) {
      setError(weak || !form.password ? 'Password must be at least 8 characters with a letter and a number' : 'Passwords do not match');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const { password, confirmPassword, passwordMode, ...rest } = form;
      const body = {
        ...rest,
        organizationIds: role?.requiresOrganization ? form.organizationIds : [],
        credential: role?.credential ? form.credential : '',
        ...(temporary ? { passwordMode: 'temporary', password, confirmPassword } : {}),
      };
      const res = await personnelApi.create(body);
      setForm(EMPTY);
      if (res.setup === 'temporary') alert.success(`${res.person.name} added with the temporary password you set`);
      else
        alert.success(
          res.invite?.sent ? `${res.person.name} added; set-password email sent` : `${res.person.name} added; invite email could not be sent — use Resend invite`
        );
      onCreated(res.person);
    } catch (err) {
      setError(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <form onSubmit={submit} autoComplete="off" data-testid="add-personnel-form">
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: 18 }}>Add Personnel</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error && <div className="alert alert-danger py-2">{error}</div>}
          <div className="row">
            <label className="col-md-4 mb-2">First name
              <input className="form-control" required value={form.firstName} onChange={set('firstName')} />
            </label>
            <label className="col-md-4 mb-2">Middle name <small className="text-muted">(optional)</small>
              <input className="form-control" value={form.middleName} onChange={set('middleName')} />
            </label>
            <label className="col-md-4 mb-2">Last name
              <input className="form-control" required value={form.lastName} onChange={set('lastName')} />
            </label>
            <label className="col-md-6 mb-2">Email
              <input type="email" className="form-control" required value={form.email} onChange={set('email')} />
            </label>
            <label className="col-md-6 mb-2">Phone
              <input type="tel" className="form-control" value={form.phone} onChange={set('phone')} />
            </label>
            <label className="col-md-6 mb-2">Role
              <select className="form-control" required value={form.role} onChange={set('role')} name="role">
                <option value="">Select a role</option>
                {roles.map((r) => (
                  <option key={r.value} value={r.value}>{r.label}</option>
                ))}
              </select>
            </label>
            {role?.credential && (
              <label className="col-md-6 mb-2">Credential
                <select className="form-control" value={form.credential} onChange={set('credential')}>
                  <option value="">Select a credential</option>
                  {(options?.credentials || []).map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
              </label>
            )}
          </div>
          {role && role.requiresOrganization && (
            <div className="mb-2">
              <b>Organization(s)</b>
              <div className="d-flex flex-wrap" style={{ gap: 12, maxHeight: 160, overflowY: 'auto' }}>
                {orgs.length === 0 && <span className="text-muted">No active organizations available</span>}
                {orgs.map((o) => (
                  <label key={o.id} style={{ fontWeight: 'normal' }}>
                    <input type="checkbox" checked={form.organizationIds.includes(o.id)} onChange={() => toggleOrg(o.id)} /> {o.name}
                  </label>
                ))}
              </div>
            </div>
          )}
          {role && !role.requiresOrganization && <p className="text-muted mb-2">Super Admins have access to all organizations.</p>}
          {canSetPassword ? (
            <fieldset className="mt-2 p-2" style={{ border: '1px solid #dee2e6', borderRadius: 6 }} data-testid="password-mode">
              <b>Password</b>
              <div className="form-check">
                <input
                  id="pm-invite"
                  type="radio"
                  className="form-check-input"
                  name="passwordMode"
                  value="invite"
                  checked={form.passwordMode === 'invite'}
                  onChange={set('passwordMode')}
                />
                <label htmlFor="pm-invite" className="form-check-label" style={{ fontWeight: 'normal' }}>
                  Send invite email so they set their own password <small className="text-muted">(recommended)</small>
                </label>
              </div>
              <div className="form-check">
                <input
                  id="pm-temporary"
                  type="radio"
                  className="form-check-input"
                  name="passwordMode"
                  value="temporary"
                  checked={form.passwordMode === 'temporary'}
                  onChange={set('passwordMode')}
                />
                <label htmlFor="pm-temporary" className="form-check-label" style={{ fontWeight: 'normal' }}>
                  Set a temporary password now
                </label>
              </div>
              {temporary && (
                <div className="row mt-1">
                  <label className="col-md-6 mb-1">Temporary password
                    <input
                      type={showPw ? 'text' : 'password'}
                      className="form-control"
                      name="new-password"
                      autoComplete="new-password"
                      required
                      value={form.password}
                      onChange={set('password')}
                    />
                  </label>
                  <label className="col-md-6 mb-1">Confirm password
                    <input
                      type={showPw ? 'text' : 'password'}
                      className="form-control"
                      name="confirm-new-password"
                      autoComplete="new-password"
                      required
                      value={form.confirmPassword}
                      onChange={set('confirmPassword')}
                    />
                  </label>
                  <div className="col-12">
                    <div className="form-check">
                      <input id="pm-show" type="checkbox" className="form-check-input" checked={showPw} onChange={(e) => setShowPw(e.target.checked)} />
                      <label htmlFor="pm-show" className="form-check-label" style={{ fontWeight: 'normal' }}>Show password</label>
                    </div>
                    {weak && <small className="text-danger d-block">At least 8 characters with a letter and a number.</small>}
                    {mismatch && <small className="text-danger d-block">Passwords do not match.</small>}
                    <small className="text-muted d-block">
                      No invite email is sent. Share the password securely and ask them to change it after signing in (Forgot Password).
                    </small>
                  </div>
                </div>
              )}
              {!temporary && (
                <small className="text-muted d-block">The new user receives an email with a link to set their own password.</small>
              )}
            </fieldset>
          ) : (
            <p className="text-muted mb-0" style={{ fontSize: 13 }}>
              No password is needed. The new user receives an email with a link to set their own password.
            </p>
          )}
        </Modal.Body>
        <Modal.Footer>
          <button type="button" className="btn btn-secondary btn-sm" onClick={onHide}>Cancel</button>
          <button type="submit" className="btn btn-primary btn-sm" disabled={busy || !form.role || (temporary && (!form.password || weak || mismatch))}>
            {busy ? 'Adding...' : temporary ? 'Add with temporary password' : 'Add and send invite'}
          </button>
        </Modal.Footer>
      </form>
    </Modal>
  );
};

export default AddPersonnelModal;
